// Practical Media game roster. Add a new entry here to publish a dedicated /games/:slug page.
// Images are served from Steam's public CDN, so no asset upload is needed.

export interface GameLink {
  label: string;
  href: string;
  primary?: boolean;
}

export interface GamePillar {
  title: string;
  desc: string;
}

export interface GameSection {
  label: string;
  // Each item is a row. `term` is the mono left side, `desc` the secondary right side (optional).
  items: { term: string; desc?: string }[];
}

export interface GameCharacter {
  name: string;
  role?: string;     // one-line description (optional — only add when confirmed by the dev)
  image: string;
}

export interface PressAsset {
  label: string;
  src: string;       // same-origin download URL
  note?: string;
}

export interface PressKit {
  pressContact: string;
  factSheet: { label: string; value: string }[];
  shortDescription: string;
  longDescription: string[];
  aboutDeveloper: string;
  screenshots: string[];   // local high-res, downloadable
  assets: PressAsset[];    // logos, key art, branding (downloadable)
  usageNote?: string;      // asset licence / credit line set by the developer
}

export interface Game {
  slug: string;
  title: string;
  subtitle?: string;        // e.g. "Tides of Olympus"
  titleLogo?: string;       // optional wordmark image shown instead of the text title
  studio: string;
  studioLogo?: string;      // optional studio icon shown next to the name
  tagline: string;          // one-liner shown under the title
  accent: string;           // per-game accent color for identity
  status: string;           // short status used on the /games index card

  // Metadata block (key-value)
  developer: string;
  genre: string;
  platforms: string;
  input?: string;           // input support, e.g. "Controller support"
  releaseDate: string;

  // Media (shown first, before the words)
  youtubeId?: string;       // trailer embed (Baiacool)
  heroVideo?: string;       // trailer video src (Memori)
  heroImage: string;        // trailer poster / fallback art
  coverImage?: string;      // /games index card art (falls back to heroImage)
  clips?: string[];         // silent looping gameplay clips (webm), GIF-style
  screenshots: string[];

  // Copy
  body: string[];           // pitch paragraphs
  pillars: GamePillar[];    // gameplay pillars (Explore / Fight / ...)
  features: string[];       // distinctive hooks
  fansOf?: { name: string; href?: string }[];  // comparable games (rendered as nofollow links if href given)
  sections?: GameSection[];      // bespoke flavour blocks (e.g. "Gods, but make them fish")
  characters?: GameCharacter[];  // portrait cast grid

  links: GameLink[];
  socials?: GameLink[];          // studio community links (YouTube, Discord, etc.)
  presskit?: PressKit;           // press kit page at /games/:slug/presskit
}

const steam = (app: string, hash: string) =>
  `https://shared.akamai.steamstatic.com/store_item_assets/steam/apps/${app}/${hash}/ss_${hash}.1920x1080.jpg`;

export const GAMES: Game[] = [
  {
    slug: 'spell-code-slingerz',
    title: 'Spell Code SlingerZ',
    titleLogo: '/games/spell-code-slingerz-logo.png',
    studio: 'Bit Forward Games',
    tagline: 'Weave codes and sling spells as a punk Y2K spell synthesizer in a 2-4 player party brawler settled by a first-to-three-wins tournament.',
    accent: '#b933ff',
    status: 'Coming soon · November 3, 2026',

    developer: 'Bit Forward Games',
    genre: '2D party brawler',
    platforms: 'Windows, macOS, Linux, Steam Deck',
    input: 'Full controller support · gamepad recommended',
    releaseDate: 'November 3, 2026',

    youtubeId: 'Aya_llv2zis',
    heroImage: steam('4500000', 'ca889f8c1de564aacc839ca106b83a9fd1497c00'),
    coverImage: '/games/spell-code-slingerz-cover.png',
    clips: [
      'https://shared.akamai.steamstatic.com/store_item_assets/steam/apps/4500000/extras/eb784f4ac104ac9a599a1d19cc185f9b.webm',
      'https://shared.akamai.steamstatic.com/store_item_assets/steam/apps/4500000/extras/55d65d54529c019355421664d0164564.webm',
    ],
    screenshots: [
      steam('4500000', 'ca889f8c1de564aacc839ca106b83a9fd1497c00'),
      steam('4500000', 'e0278598477f938d783b16f78d88ec23668be05a'),
      steam('4500000', '2a81b0b21e11926eb478f11461f7354b51595d2e'),
      steam('4500000', 'd5b4d44ca518a229b13191f2e0b380be927b15e7'),
      steam('4500000', 'ac5763a33253f1a62712f2556d983aed89bb2131'),
      steam('4500000', '75e3546d177f7c6a04ec668d1d0ffc6c9d6b8a49'),
      steam('4500000', '748129c79ad0e23382f5d04a5bb28da9bebde7d6'),
    ],

    body: [
      'Spell Code SlingerZ is a 2-4 player party brawler for couch or online play. Load in, beat down the opposition, draft a new Spellcode to shape your build, and beat them down again in a first-to-three-wins tournament where the fight and the fighter both evolve round to round.',
      'The twist is the Code Weave. Instead of memorizing input strings for every special, you drop into a weave state, take the time you need to prime and store a Spellcode, then unleash it when the moment is right. Easy to pick up on the couch, deep enough to keep a lobby arguing about builds all night.',
    ],
    pillars: [
      { title: 'Weave', desc: 'Enter the Code Weave state to prime and store a Spellcode on your own time, then let it fly. No fighting-game input strings required.' },
      { title: 'Build', desc: 'Draft one of up to nine Spellcodes between rounds, stack duplicates, and mix active and passive picks into a kit that is yours.' },
      { title: 'Style', desc: 'Each color-coded Developer Collective has its own playstyle and fantasy. Commit to one or splice several into something new.' },
      { title: 'Brawl', desc: '2-4 players, couch or online, in a first-to-three tournament where a pseudo-random shop makes no two matches the same.' },
    ],
    features: [
      '2-4 player party brawling, online and local couch play',
      'Code Weave: prime and store spells on your own time instead of memorizing inputs',
      'Up to 9 Spellcodes to draft between rounds, with stackable active and passive picks',
      'Color-coded Developer Collectives, each with its own playstyle to master or mix',
      'A pseudo-random shop and duplicate Spellcodes make every match and build different',
      'Y2K aesthetic with neon signs, bright colors, and a custom early-2000s R&B, EDM, and House score',
      'Remote Play Together and full controller support',
    ],
    fansOf: [
      { name: 'Brawlhalla', href: 'https://store.steampowered.com/app/291550/Brawlhalla/' },
      { name: 'TowerFall Ascension', href: 'https://store.steampowered.com/app/251470/TowerFall_Ascension/' },
      { name: 'ROUNDS', href: 'https://store.steampowered.com/app/1557740/ROUNDS/' },
    ],

    presskit: {
      pressContact: 'bitforwardgames@gmail.com',
      factSheet: [
        { label: 'Developer', value: 'Bit Forward Games' },
        { label: 'Release date', value: 'November 3, 2026' },
        { label: 'Store', value: 'Steam' },
        { label: 'Build platforms', value: 'Windows, macOS, Linux, Steam Deck' },
        { label: 'Players', value: '2-4 · local couch and online' },
        { label: 'Price', value: '$7.99' },
        { label: 'Genres', value: '2D platform fighter, versus, party, action, indie' },
        { label: 'Netcode', value: 'P2P deterministic rollback' },
        { label: 'Modes', value: 'Local multiplayer, online multiplayer' },
        { label: 'Languages', value: 'English' },
      ],
      shortDescription:
        'Weave codes and sling spells as a punk Y2K Spell Code Synthesizer in this 2-4 player, couch/online, 2D party brawler. Annihilate the opposition, acquire powerful new Spellcodes, and beat them down again in a first-to-3-wins style tournament.',
      longDescription: [
        'Spell Code SlingerZ is a fast paced, 2D party brawler with a striking visual style and rollback netcode. The work-in-progress playtest, available free on Steam, showcases the three core features: spell weaving, bounties, and custom build creation.',
        'The spell weaving mechanic and control scheme are designed to let players perform complex actions from a minimalist set of controls. Instead of pressing a dedicated button to cast, players hold the code button, enter a short directional sequence, and release to fire the matching .exe Spellcode. Paired with the bounty mechanic, which gives struggling players a way back in without punishing the player in front, it lets new and veteran players enjoy the same lobby. Custom build creation then lets everyone experiment until they find a playstyle that fits.',
        'The visual style leans on heavily weighted line art, a limited palette, and cartoony characters in a cartoony world. Spells are the exception: they are made in pixel art as digital intrusions into the hand-drawn scene, a deliberate mixed-media contrast that keeps the action readable even amid the chaos and speed of four-player combat.',
        'Online play matters as much as couch play here. To keep the combat fast while players are apart, the game runs on peer-to-peer deterministic rollback networking built on the BestoNet library and integrated with the Steamworks API. When latency makes remote inputs arrive late, the engine rewinds the simulation, applies the correct inputs, and fast-forwards back to the present frame.',
      ],
      aboutDeveloper:
        'Bit Forward Games is the five-person team behind Spell Code SlingerZ. The game has been in development since August 2025, when it began as a Master\'s program capstone project with five internal developers and two external artists, guided by three committee members. Since graduating in May 2026 the five internal developers have continued the project independently, with the full release set for November 3, 2026.',
      usageNote:
        'Bit Forward Games makes these images and videos free to use as long as they are credited. Spell Code SlingerZ is in active development, so the details on this page are subject to change.',
      screenshots: [
        '/games/spell-code-slingerz-press/screenshots/spell-code-slingerz-screenshot-1.jpg',
        '/games/spell-code-slingerz-press/screenshots/spell-code-slingerz-screenshot-2.jpg',
        '/games/spell-code-slingerz-press/screenshots/spell-code-slingerz-screenshot-3.jpg',
        '/games/spell-code-slingerz-press/screenshots/spell-code-slingerz-screenshot-4.jpg',
        '/games/spell-code-slingerz-press/screenshots/spell-code-slingerz-screenshot-5.jpg',
        '/games/spell-code-slingerz-press/screenshots/spell-code-slingerz-screenshot-6.jpg',
        '/games/spell-code-slingerz-press/screenshots/spell-code-slingerz-screenshot-7.jpg',
        '/games/spell-code-slingerz-press/screenshots/spell-code-slingerz-screenshot-8.jpg',
        '/games/spell-code-slingerz-press/screenshots/spell-code-slingerz-screenshot-9.jpg',
      ],
      assets: [
        { label: 'Logo (wordmark)', src: '/games/spell-code-slingerz-logo.png', note: 'PNG · 1280×439 · transparent' },
        { label: 'Main capsule', src: '/games/spell-code-slingerz-cover.png', note: 'PNG · 1232×706' },
        { label: 'Vertical capsule', src: '/games/spell-code-slingerz-press/spell-code-slingerz-capsule-vertical.png', note: 'PNG · 748×896' },
        { label: 'Key art (16:9)', src: '/games/spell-code-slingerz-press/spell-code-slingerz-keyart-wide.jpg', note: 'JPG · 3840×2160' },
        { label: 'Poster (portrait)', src: '/games/spell-code-slingerz-press/spell-code-slingerz-poster.jpg', note: 'JPG · 2814×3752' },
        { label: 'Cast cutout', src: '/games/spell-code-slingerz-keyart.png', note: 'PNG · 5120×1280 · transparent' },
        { label: 'Game icon', src: '/games/spell-code-slingerz-press/spell-code-slingerz-icon.png', note: 'PNG · 512×512' },
        { label: 'Studio logo', src: '/games/spell-code-slingerz-press/bitforward-logo.png', note: 'Bit Forward Games · transparent' },
        { label: 'Studio mark', src: '/games/spell-code-slingerz-press/bitforward-mark.png', note: 'Bit Forward Games · 1024×1024' },
      ],
    },

    links: [
      { label: 'Wishlist on Steam', href: 'https://store.steampowered.com/app/4500000/Spell_Code_SlingerZ/', primary: true },
      { label: 'Play on itch.io', href: 'https://fmpo.itch.io/spell-code' },
    ],
    socials: [
      { label: 'Discord', href: 'https://discord.gg/SyrwwshYfB' },
      { label: 'YouTube', href: 'https://www.youtube.com/@BitForwardGames' },
      { label: 'Instagram', href: 'https://www.instagram.com/bitforwardgames/' },
      { label: 'TikTok', href: 'https://www.tiktok.com/@bitforwardgames' },
      { label: 'LinkedIn', href: 'https://www.linkedin.com/company/bitforwardgames/' },
    ],
  },
  {
    slug: 'baiacool',
    title: 'Baiacool',
    subtitle: 'Tides of Olympus',
    studio: 'Sasquatch with Socks',
    studioLogo: '/games/sasquatch-icon.png',
    tagline: 'A pufferfish who can only say "Cool" must save Poseidon in an absurd underwater take on Greek mythology.',
    accent: '#19b5c4',
    status: 'Coming soon · demo on Steam',

    developer: 'Sasquatch with Socks',
    genre: 'Top-down comedy adventure',
    platforms: 'Windows, macOS, Linux',
    input: 'Controller support',
    releaseDate: 'Coming soon',

    youtubeId: '8IYVk5hPDQo',
    heroImage: steam('3797250', '81db9e0340db9691852df73254039d2976a586a9'),
    coverImage: '/games/baiacool-cover-illustrated.png',
    screenshots: [
      steam('3797250', '3b4b687586f05763b54c2b550ba6c2646f800862'),
      steam('3797250', '63accda39159c4bfc32a71f349e497b9fe13927d'),
      steam('3797250', '1c41b98b0147381abace4f4ba09d15b84b67c97f'),
      steam('3797250', '51c9078d6a9aa172f2a1bc11a202b46d3d9dd764'),
      steam('3797250', 'f1ecdfd1a18697c200f25291ff1e2e4781d95e16'),
      steam('3797250', '9853b70d7cbf8333fcb0bf465db4467f7e9eb01c'),
    ],

    body: [
      'What if Greek mythology happened at the bottom of the sea, Poseidon disappeared, and the only hero available was a pufferfish who can only say "Cool"? Welcome to an underwater version of Olympus, where the gods have been reimagined as sea creatures, divine drama is still very much alive, and the fate of the ocean somehow falls to Baiacool: a round, stubborn pufferfish with a confident attitude and a vocabulary made of exactly one word.',
      'He is not alone. The Mystic Stick, a talking weapon with far more to say than its owner, joins him to investigate Poseidon\'s disappearance and face marine versions of Greek gods who are every bit as dramatic and unreasonable as you would expect. A serious mythological problem, handled by deeply unserious people.',
    ],
    pillars: [
      { title: 'Explore', desc: 'Themed mythological regions, secrets, and unlockable powers.' },
      { title: 'Fight', desc: 'Action combat and boss fights against gods reimagined as sea creatures.' },
      { title: 'Puzzle', desc: 'Environmental puzzles woven through every underwater domain.' },
      { title: 'Cool', desc: 'A protagonist who answers nearly every situation with the same word.' },
    ],
    features: [
      'A talking weapon companion with far too much personality',
      'Greek gods reimagined as dramatic sea creatures',
      'Unlockable mix-and-match skins',
      'Compact and fast-paced, without overstaying its welcome',
    ],
    // Names + art only for now. Add `role` lines once João confirms the real bios.
    characters: [
      { name: 'Baiacool', image: '/games/baiacool-characters/baiacool.png' },
      { name: 'The Mystic Stick', image: '/games/baiacool-characters/mystic-stick.png' },
      { name: 'Ares', image: '/games/baiacool-characters/ares.png' },
      { name: 'Athena', image: '/games/baiacool-characters/athena.png' },
      { name: 'Hephaestus', image: '/games/baiacool-characters/hephaestus.png' },
      { name: 'Hecate', image: '/games/baiacool-characters/hecate.png' },
      { name: 'Crazy Crab', image: '/games/baiacool-characters/crazy-crab.png' },
      { name: 'Blob', image: '/games/baiacool-characters/blob.png' },
    ],
    fansOf: [
      { name: 'Turnip Boy Commits Tax Evasion', href: 'https://store.steampowered.com/app/1205450/Turnip_Boy_Commits_Tax_Evasion/' },
      { name: 'Death\'s Door', href: 'https://store.steampowered.com/app/894020/Deaths_Door/' },
      { name: 'TUNIC', href: 'https://store.steampowered.com/app/553420/TUNIC/' },
    ],

    presskit: {
      pressContact: 'support@baiacool.com.br',
      factSheet: [
        { label: 'Developer', value: 'Sasquatch with Socks' },
        { label: 'Creator', value: 'João Sass' },
        { label: 'Based in', value: 'Brazil' },
        { label: 'Genre', value: 'Top-down comedy adventure with puzzles' },
        { label: 'Platforms', value: 'Windows, macOS, Linux (Steam)' },
        { label: 'Players', value: 'Single-player' },
        { label: 'Release date', value: 'TBA · demo available on Steam' },
        { label: 'Published by', value: 'Practical Media' },
      ],
      shortDescription:
        'Baiacool: Tides of Olympus is a funny top-down adventure where Greek mythology goes underwater and saving Poseidon somehow falls to a pufferfish who can only say "Cool."',
      longDescription: [
        'From Brazilian solo developer João Sass, under the studio name Sasquatch with Socks, comes a funny top-down adventure that asks: what if Greek mythology happened at the bottom of the sea, Poseidon disappeared, and the only hero available was a pufferfish who can only say "Cool"?',
        'Welcome to an underwater version of Olympus, where gods and mythical beings have been reimagined as sea creatures, divine drama is still very much alive, and Poseidon has mysteriously vanished after sending a cryptic warning about an incoming war. The ocean is unstable, the gods are suspicious, and somehow the fate of everything falls to Baiacool: a round, stubborn pufferfish with a confident attitude, questionable survival instincts, and a vocabulary made of exactly one word.',
        'He is not alone. The Mystic Stick, a talking weapon with much more to say than its owner, joins him to investigate Poseidon\'s disappearance, explore strange underwater domains, and face marine versions of Greek gods who are just as dramatic, dangerous, and unreasonable as you would expect.',
        'The game blends top-down exploration, action combat, environmental puzzles, boss fights, and absurd character-driven humor into a compact indie adventure. Explore themed mythological areas, battle enemies, solve puzzles, unlock new powers, meet bizarre NPCs, and uncover what really happened to Poseidon, all while Baiacool remains emotionally consistent. He says "Cool."',
      ],
      aboutDeveloper:
        'Sasquatch with Socks is an indie game studio from Brazil, created by solo developer João Sass. With around 10 years of experience as a developer, João is now building Baiacool: Tides of Olympus, a funny and colorful indie adventure inspired by Greek mythology, absurd humor, and expressive 2D games.',
      screenshots: [
        '/games/baiacool-press/screenshots/baiacool-screenshot-1.jpg',
        '/games/baiacool-press/screenshots/baiacool-screenshot-2.jpg',
        '/games/baiacool-press/screenshots/baiacool-screenshot-3.jpg',
        '/games/baiacool-press/screenshots/baiacool-screenshot-4.jpg',
        '/games/baiacool-press/screenshots/baiacool-screenshot-5.jpg',
        '/games/baiacool-press/screenshots/baiacool-screenshot-6.jpg',
      ],
      assets: [
        { label: 'Logo (wordmark)', src: '/games/baiacool-logo.png', note: 'PNG · transparent' },
        { label: 'Key art (16:9)', src: '/games/baiacool-cover.png', note: 'PNG · 1920×1080' },
        { label: 'Wide banner', src: '/games/baiacool-press/baiacool-banner-wide.png', note: 'PNG · 3840×1240' },
        { label: 'Vertical capsule', src: '/games/baiacool-press/baiacool-capsule-vertical.png', note: 'PNG · 600×900' },
        { label: 'Game icon', src: '/games/baiacool-press/baiacool-icon.png', note: 'PNG · 512×512' },
        { label: 'Studio logo', src: '/games/baiacool-press/sasquatch-logo-white.png', note: 'Sasquatch with Socks' },
      ],
    },

    links: [
      { label: 'Wishlist on Steam', href: 'https://store.steampowered.com/app/3797250/Baiacool__Tides_of_Olympus/', primary: true },
    ],
    socials: [
      { label: 'Website', href: 'https://www.baiacool.com.br/' },
      { label: 'YouTube', href: 'https://www.youtube.com/@sasquatchwithsocks' },
      { label: 'Discord', href: 'https://discord.gg/uXdPnRAfCE' },
    ],
  },
  {
    slug: 'arcane-audit',
    title: 'Arcane Audit',
    studio: 'Broken Beaker Studios',
    tagline: 'An IRS-themed souls-like. Battle wizards, monsters, and middle managers through the offices of a magically corrupted business.',
    accent: '#ff4d3d',
    status: 'Coming soon · demo on Steam',

    developer: 'Broken Beaker Studios',
    genre: 'Souls-like action RPG',
    platforms: 'Windows, Linux · Steam Deck',
    input: 'Controller support',
    releaseDate: 'Coming soon · demo on Steam',

    youtubeId: 'OWPxKIqil20',
    heroImage: steam('2381770', '9d4b28cbc7f3d423f03f84ca2f9ba95b562c5d35'),
    coverImage: '/games/arcane-audit-cover.jpg',
    screenshots: [
      '/games/arcane-audit-cover.jpg',
      steam('2381770', 'cfdaf95cb49468574834ccadf8dcc92418857c98'),
      steam('2381770', '045cb1f7233b39751db045e70e2f2fc35c2ed36b'),
      steam('2381770', '2733a7d0adf155663786948382a805e950969bc8'),
      steam('2381770', '1d74803013f6bdedc8c078c8ef0fefc0d08c66aa'),
      steam('2381770', '21c5cf14546b14349a53fa8adc7bfd2d2553c10b'),
      steam('2381770', '9638e257c7df2d961a0a0067d4b5bf4967b13de5'),
    ],

    body: [
      'When a company\'s CEO gets corrupted by dark magic, the first thing to go is the paperwork. The tax forms never get filed, the office twists into something impossible, and the only person willing to do anything about it is you: an IRS agent sent in to audit the whole rotten place by force. Arcane Audit is a souls-like about fighting your way through a haunted corporate tower to recover the proper tax forms.',
      'Every department is its own hazard. Fire wizards run IT, ice golems staff accounting, and each floor is guarded by a manager complicit in the fraud. Collect fines from everything you defeat, spend them to grow more lethal, and dig through the offices for arcane secrets that unlock new weapons and abilities. A serious problem with the tax code, solved by extremely unserious means.',
    ],
    pillars: [
      { title: 'Fight', desc: 'Deliberate souls-like combat built on dodging, reading attacks, and picking the right weapon.' },
      { title: 'Explore', desc: 'An office building twisted by magic, full of secrets and department-themed hazards.' },
      { title: 'Grow', desc: 'Spend collected fines to level up and uncover new weapons and arcane abilities.' },
      { title: 'Audit', desc: 'Literal boss fights against the middle managers covering up the fraud.' },
    ],
    features: [
      'Souls-like combat built around dodging, reading enemies, and swapping weapons',
      'Department-themed enemies, from fire wizards in IT to ice golems in accounting',
      'Boss fights against the managers complicit in the tax fraud',
      'Collect fines from fallen foes and spend them to grow more powerful',
      'Arcane secrets hidden through the offices that grant new abilities',
    ],
    fansOf: [
      { name: 'Have a Nice Death', href: 'https://store.steampowered.com/app/1740720/Have_a_Nice_Death/' },
      { name: 'Hades II', href: 'https://store.steampowered.com/app/1145350/Hades_II/' },
      { name: 'Elden Ring', href: 'https://store.steampowered.com/app/1245620/ELDEN_RING/' },
    ],

    links: [
      { label: 'Wishlist on Steam', href: 'https://store.steampowered.com/app/2381770/Arcane_Audit/', primary: true },
    ],
    socials: [
      { label: 'Website', href: 'https://www.brokenbeakerstudio.com/' },
      { label: 'Discord', href: 'https://discord.gg/U56DXmBERu' },
      { label: 'Twitter', href: 'https://twitter.com/Broken_Beaker' },
    ],
  },
  {
    slug: 'memori',
    title: 'Memori',
    studio: 'PracticalNPC',
    tagline: 'Precision platforming built for speedrunners and players who want to perfect the run.',
    accent: '#1DB954',
    status: 'In Early Access · Steam',

    developer: 'PracticalNPC (solo / Godot)',
    genre: 'Precision platformer',
    platforms: 'Windows, Linux',
    input: 'Controller support',
    releaseDate: 'In Early Access (since Jul 28, 2025)',

    youtubeId: 'Mg0y5XJFBcE',
    heroImage: steam('1712700', '1c71add73869a4736d4f6d0ac42667edcd00fd4e'),
    coverImage: '/games/memori-cover.png',
    clips: [
      '/games/memori-clips/memori-1.webm',
      '/games/memori-clips/memori-2.webm',
      '/games/memori-clips/memori-3.webm',
      '/games/memori-clips/memori-4.webm',
      '/games/memori-clips/memori-5.webm',
      '/games/memori-clips/memori-6.webm',
    ],
    screenshots: [
      steam('1712700', '1c71add73869a4736d4f6d0ac42667edcd00fd4e'),
      steam('1712700', '5b40c02db77bffed346948729e1ea76a834de89b'),
      steam('1712700', '9354b0c17fe406eff15414af8dad4f836f2a6f52'),
      steam('1712700', '64fba3581114c22e1f7eac95233b36179c62e61e'),
      steam('1712700', '9da9adbdfc26dea49620b536d1e061c4740e0f90'),
      steam('1712700', '7c06e8bf3c3d741e8e93290da64c23a5d159612c'),
    ],

    body: [
      'Master precision platforming in Memori. Navigate challenging rooms, discover hidden mechanics, and perfect your runs in a world designed for speedrunners and skilled players. Every screen is a small, deliberate puzzle of movement.',
      'Memori is the debut game from PracticalNPC, founder of the SoloDevelopment community and the developer behind Practical Media itself. It is the kind of game the studio exists to champion: tight, handmade, and made by one person.',
    ],
    pillars: [
      { title: 'Precision', desc: 'Tight, responsive platforming where every input matters.' },
      { title: 'Discover', desc: 'Hidden mechanics that reward curiosity and mastery.' },
      { title: 'Perfect', desc: 'Run-perfecting design built for speedrunners.' },
    ],
    features: [
      'Handcrafted rooms, each its own movement puzzle',
      'Built for speedrunners and skilled players',
      'Hidden mechanics to uncover',
    ],

    links: [
      { label: 'Play the Early Access on Steam', href: 'https://store.steampowered.com/app/1712700/Memori/', primary: true },
    ],
  },
];

export const getGame = (slug?: string): Game | undefined =>
  GAMES.find((g) => g.slug === slug);
